import { useState, useEffect, useContext } from 'react'
import AuthContext from 'contexte/AuthContext'
import '../notifications.css'
import './notificationBuilder.css'
import Notification from 'components/notifications/Notification'
import { launchError, launchSuccess } from 'components/notifications'
import { useTranslation } from 'react-i18next';
import DashboardLayout from 'components/layout/dashboard/DashboardLayout'
import { searchUsers } from 'services/fetchUsers'
import { fetchTickers } from 'services/tickersServices'
import SearchUser from 'components/SearchUser/SearchUser'
import { fetchLastUserPosts } from 'services/postServices'
import { fetchStructuredProducts } from 'services/structuredProductsServices'
import SearchStructuredProducts from 'components/SearchStructuredProducts/SearchStructuredProducts'
import { notifyUsers } from 'services/userServices'
import SearchTicker from 'components/SearchTicker/SearchTicker'
import DateRangeSelector from 'components/Filters/DateSelector/DateSelector'
import { useNavigate, useParams } from 'react-router-dom'
import { createNotification, fetchNotification, updateNotification } from 'services/notificationsServices'
import { fetchSignalProviderList } from 'services/signalProvidersServices'
import SearchSignalProvider from 'components/SearchSignalProvider/SearchSignalProvider'
import ConfirmModal from 'components/ConfirmModal/ConfirmModal'
import { fetchCioSegments } from 'services/segmentServices'

const buildEmptyNotification = () => ({
  title: '',
  message: '',
  type: '',
  isSettable: false,
  ticker: {},
  product: {},
  owner: {},
  post: {},
  signal_provider: {},
  segment_id: ''
})


const NotificationBuilder = () => {


  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext)
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [tickers, setTickers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [structuredProducts, setStructuredProducts] = useState([]);
  const [signalProviders, setSignalProviders] = useState([]);
  const [mustSelectSp, setMustSelectSp] = useState(false);
  const [selectedDate, setSelectedDate] = useState({
    date1: { startDate: new Date(), endDate: null },
    date2: { startDate: null, endDate: null },
  });
  const [isValid, setIsValid] = useState(false);
  const [confirmBroadcast, setConfirmBroadcast] = useState(false);
  const [segments, setSegments] = useState([])
  const [segmentsStatus, setSegmentsStatus] = useState('loading') // 'loading' | 'loaded' | 'error'

  const [notification, setNotification] = useState(buildEmptyNotification());

  const handleInputChange = (field, value) => {
    const types = ['product', 'ticker', 'owner', 'simple', 'signal_provider'];
    const updatedNotif = { ...notification, [field]: value };
    console.log({ updatedNotif })
    const index = types.indexOf(field);
    if (index > -1) {
      types.splice(index, 1);
      for (let type of types) {
        updatedNotif[type] = {}
      }
      if (field === 'owner') setPosts([])
    }
    setNotification(updatedNotif)
  }

  const notify = async (isBroadcast = false) => {
    try {
      setLoading(true);
      const body = {
        type: notification.type,
        title: notification.title,
        message: notification.message,
      }
      // le test part au segment de test de l'API, l'envoi réel au segment Customer.io choisi
      if (isBroadcast)
        body['segment_id'] = notification.segment_id
      else
        body['target'] = 'test'
      if (notification.isSettable) {
        const date = new Date(selectedDate.date1.startDate);
        date.setUTCSeconds(0, 0);
        body['date'] = date
      }
      switch (notification.type) {
        case 'post':
          body['owner_uuid'] = notification.owner.uuid
          body['post_uuid'] = notification.post.getstream_uuid
          break;
        case 'stock':
          body['ticker'] = notification.ticker.ticker
          break;
        case 'structured_product':
          body['product_id'] = notification.product._id
          break;
        case 'signal_provider':
          body['signal_provider_uuid'] = notification.signal_provider.uuid;
          body['is_bot'] = !!notification.signal_provider.is_bot;
          break;
        default:
          break;
      }
      if (id) {
        await updateNotification(user, id, body)
        launchSuccess(t('notifications.notifications.update.success'))
        navigate('/notifications');
      } else {
        await createNotification(user, body)
        launchSuccess(t('notifications.notifications.create.success'))
        if (isBroadcast)
          setNotification(buildEmptyNotification())
      }
    } catch (e) {
      console.error(e);
      if (id)
        launchError(t('notifications.notifications.update.error'))
      else
        launchError(t('notifications.notifications.create.error'))
    } finally {
      setLoading(false)
      setConfirmBroadcast(false)
    }
  }


  const fetchNotif = async () => {
    try {
      const data = await fetchNotification(user, id);
      if (data.date) {
        setSelectedDate({
          date1: { startDate: new Date(data.date), endDate: null },
          date2: { startDate: null, endDate: null },
        })
        data['isSettable'] = true
      }

      setNotification({ ...data, segment_id: data.segment_id ? String(data.segment_id) : '' });
    } catch (error) {
      launchError(t('notifications.users.notify.error'))
    }
  }

  useEffect(() => {
    if (id) {
      fetchNotif()
    }
  }, [id])

  // segments Customer.io (même liste que pour les campagnes)
  const loadSegments = () => {
    setSegmentsStatus('loading')
    fetchCioSegments(user)
      .then((data) => {
        setSegments(data)
        setSegmentsStatus('loaded')
      })
      .catch(() => setSegmentsStatus('error'))
  }

  useEffect(() => {
    loadSegments()
  }, [])


  useEffect(() => {
    const fetchUserPosts = async () => {
      try {
        const data = await fetchLastUserPosts(user, notification.owner?.uuid, 30)
        setPosts(data);
        handleInputChange('post', data?.[0] || {})
      } catch (error) {
        launchError();
        setPosts([]);
      }
    }
    if (notification.owner?.uuid) {
      fetchUserPosts();
    }
  }, [user, notification.owner])


  useEffect(() => {
    const valid = notification.title && notification.message && (!notification.isSettable || (notification.isSettable && selectedDate.date1.startDate > new Date())) && (
      (notification.type === 'post' && notification.owner?.uuid && notification.post?._id) ||
      (notification.type === 'stock' && notification.ticker?.id) ||
      (notification.type === 'structured_product' && notification.product?._id) ||
      (notification.type === 'simple') ||
      (notification.type === 'signal_provider' && ((mustSelectSp && notification.signal_provider?.uuid) || !mustSelectSp))
    )
    setIsValid(valid)
  }, [notification.title, notification.message, notification.type, notification.post, notification.product, notification.owner, notification.ticker, selectedDate.date1.startDate, notification.signal_provider, mustSelectSp])



  const handleSearchUsers = async (search) => {
    if (search.length > 0) {
      try {
        setLoading(true)
        const fetchUsers = await searchUsers(user, search)
        setUsers(fetchUsers)
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    } else {
      setUsers([])
    }
  }

  const handleSearchTickers = async (search) => {
    if (search.length > 0) {
      try {
        setLoading(true)
        const fetchedTickers = await fetchTickers({ user, page: 0, limit: 10, search })
        setTickers(fetchedTickers)
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    } else {
      setTickers([])
    }
  }

  const handleSearchSp = async (search) => {
    if (search.length > 0) {
      try {
        setLoading(true)
        const data = await fetchStructuredProducts({ user, page: 0, limit: 10, search })
        setStructuredProducts(data)
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    } else {
      setStructuredProducts([])
    }
  }

  useEffect(() => {
    const fetchSignalProviders = async () => {
      try {
        const data = await fetchSignalProviderList(user)
        setSignalProviders(data)
      } catch (e) {
        console.error(e)
        launchError();
        setSignalProviders([]);
      }
    }
    if (notification?.type === 'signal_provider' && signalProviders.length === 0) {
      fetchSignalProviders();
    }
  }, [user, notification.type])

  const handlePostWriter = (writer) => {
    handleInputChange('owner', writer, [{ field: 'post', value: {} }]);
  }

  const getComponent = (type) => {
    switch (type) {
      case 'post':
        return (
          <>
            <div className='notifications_field'>
              <label className='notifications_field_label' htmlFor='user-search'>Rédacteur</label>
              <SearchUser
                id='user-search'
                selectUser={handlePostWriter}
                selectedUser={notification.owner}
                users={users}
                handleSearch={handleSearchUsers}
              />
            </div>
            {notification.owner?.uuid && <div className='notifications_field'>
              <label className='notifications_field_label' htmlFor="user-posts-select">Derniers posts de {notification.owner?.firstName} {notification.owner?.lastName}</label>
              <select name="user-posts"
                id="user-posts-select"
                value={Math.max(0, posts.findIndex((post) => post._id === notification.post?._id))}
                onChange={(e) => handleInputChange('post', posts[e.target.value])}>
                {posts.map((post, index) => (<option key={index} value={index}>{post.desc}</option>))}
              </select>
            </div>}
          </>
        )
      case 'stock':
        return (
          <div className='notifications_field'>
            <label className='notifications_field_label' htmlFor="search_ticker">Action</label>
            <SearchTicker
              id='search_ticker'
              setSelectedTicker={(value) => handleInputChange('ticker', value)}
              selectedTicker={notification.ticker}
              tickers={tickers}
              handleSearch={handleSearchTickers}
            />
          </div>
        )
      case 'structured_product':
        return (
          <div className='notifications_field'>
            <label className='notifications_field_label' htmlFor="search_sp">Produit structuré</label>
            <SearchStructuredProducts
              id='search_sp'
              setSelectedSp={(value) => handleInputChange('product', value)}
              selectedSp={notification.product}
              structuredProducts={structuredProducts}
              handleSearch={handleSearchSp}
            />
          </div>
        )
      case 'signal_provider':
        return (
          <>
            <div className='notifications_switch_row'>
              <div className='notifications_switch_label'><p id='select-sp-label'>Cibler un signal provider précis</p></div>
              <label className='toggle notifications_toggle'>
                <input className="toggle-checkbox"
                  type="checkbox"
                  role="switch"
                  aria-labelledby='select-sp-label'
                  checked={mustSelectSp}
                  onChange={(e) => setMustSelectSp(e.target.checked)}
                />
                <div className="toggle-switch"></div>
              </label>
            </div>
            {
              mustSelectSp && <div className='notifications_field'>
                <label className='notifications_field_label' htmlFor="search_sp">Signal provider</label>
                <SearchSignalProvider
                  id='search_sp'
                  selectSignalProvider={(value) => handleInputChange('signal_provider', value)}
                  selectedSignalProvider={notification.signal_provider}
                  signalProviders={signalProviders}
                  label={'Liste des signal providers'}
                />
              </div>
            }
          </>
        )
      default:
        return null;
    }
  }

  const linkedField = getComponent(notification.type)
  const selectedSegment = segments.find(({ id }) => id === notification.segment_id)
  const targetLabel = selectedSegment?.name || `segment ${notification.segment_id}`
  // notification programmée avec un segment qui n'est plus dans la liste Customer.io
  const segmentMissing = segmentsStatus === 'loaded' && notification.segment_id && !selectedSegment

  return (
    <DashboardLayout page={t(id ? "pages_name.notifications_edit" : "pages_name.notifications_new")}>
      <div className='notifications_page'>
        <Notification />
        <div className='notifications_title_row'>
          <h1 className='notifications_page_title'>{id ? 'Modifier la notification' : 'Nouvelle notification'}</h1>
          <button className='notifications_secondary_button' onClick={() => navigate('/notifications')}>Retour à la liste</button>
        </div>
        <p className='notifications_hint'>Testez d'abord la notification (envoi à l'équipe de test), puis choisissez un segment Customer.io pour l'envoyer à ses utilisateurs.</p>

        <section aria-labelledby='notif-content-title'>
          <div className='notifications_title_row'>
            <h2 id='notif-content-title'>Contenu</h2>
          </div>
          <div className='notifications_card'>
            <div className='notifications_field'>
              <label className='notifications_field_label' htmlFor="notification-type-select">Type</label>
              <select name="notification-type"
                id="notification-type-select"
                value={notification.type}
                onChange={(e) => handleInputChange('type', e.target.value)}>
                <option value="">Choisissez un type</option>
                <option value="simple">Ne rien lier</option>
                <option value="post">Lier un post</option>
                <option value="structured_product">Lier un produit structuré</option>
                <option value="stock">Lier une action</option>
                <option value="signal_provider">Lier les signal providers</option>
              </select>
            </div>
            {linkedField}
            <div className='notifications_field'>
              <label className='notifications_field_label' htmlFor="title">Titre</label>
              <input
                id="title"
                type="text"
                value={notification.title}
                onInput={(e) => handleInputChange('title', e.target.value)} />
            </div>
            <div className='notifications_field'>
              <label className='notifications_field_label' htmlFor="message">Message</label>
              <textarea
                id="message"
                rows={4}
                value={notification.message}
                onInput={(e) => handleInputChange('message', e.target.value)} />
            </div>
          </div>
        </section>

        <section aria-labelledby='notif-sending-title'>
          <div className='notifications_title_row'>
            <h2 id='notif-sending-title'>Envoi</h2>
          </div>
          <div className='notifications_card'>
            <div className='notifications_switch_row'>
              <div className='notifications_switch_label'><p id='programmable-label'>Programmer l'envoi</p></div>
              <label className='toggle notifications_toggle'>
                <input className="toggle-checkbox"
                  type="checkbox"
                  role="switch"
                  aria-labelledby='programmable-label'
                  aria-describedby='programmable-hint'
                  checked={notification.isSettable}
                  onChange={(e) => handleInputChange('isSettable', e.target.checked)}
                />
                <div className="toggle-switch"></div>
              </label>
            </div>
            <p id='programmable-hint' className='notifications_switch_hint'>Désactivé, la notification part dès l'envoi.</p>
            {notification.isSettable && <div className='notifications_field'>
              <label className='notifications_field_label' htmlFor="date_selector">Date et heure d'envoi</label>
              <DateRangeSelector
                id='date_selector'
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                withComparaison={false}
                simpleDate={true}
                showTime={true}
                minDate={new Date()}
              />
            </div>}
            <div className='notifications_field notifications_field_separated'>
              <label className='notifications_field_label' htmlFor="target_selector">Segment</label>
              <select id='target_selector'
                value={notification.segment_id || ''}
                disabled={segmentsStatus !== 'loaded'}
                aria-describedby='target_selector_hint'
                onChange={(e) => handleInputChange('segment_id', e.target.value)}>
                <option value=''>{segmentsStatus === 'loading' ? 'Chargement des segments…' : 'Choisissez un segment'}</option>
                {segmentMissing && <option value={notification.segment_id}>Segment {notification.segment_id} (introuvable dans Customer.io)</option>}
                {segments.map(({ id, name }) => <option key={id} value={id}>{name}</option>)}
              </select>
              {segmentsStatus === 'error' ? (
                <p id='target_selector_hint' className='notifications_helper' role='alert'>
                  Impossible de charger les segments Customer.io. <button type='button' className='notifications_row_button' onClick={loadSegments}>Réessayer</button>
                </p>
              ) : (
                <p id='target_selector_hint' className='notifications_helper'>
                  {selectedSegment?.description || 'Segments Customer.io : la notification est envoyée aux utilisateurs du segment.'}
                </p>
              )}
            </div>
          </div>
        </section>

        <div className='notifications_actions'>
          {!isValid && <p className='notifications_helper'>Renseignez le type, le titre, le message et l'élément lié (et une date future si l'envoi est programmé).</p>}
          {isValid && !notification.segment_id && <p className='notifications_helper'>Choisissez un segment pour envoyer la notification.</p>}
          <button onClick={() => notify()} className='notifications_secondary_button' disabled={!isValid || loading}>Tester</button>
          <button onClick={() => setConfirmBroadcast(true)} className='notifications_primary_button notifications_danger_button' disabled={!isValid || loading || !notification.segment_id}>Envoyer au segment</button>
        </div>

        <ConfirmModal
          isOpen={confirmBroadcast}
          loading={loading}
          title={notification.isSettable ? "Programmer l'envoi ?" : 'Envoyer la notification ?'}
          message={`« ${notification.title} » sera envoyée au segment « ${targetLabel} »${notification.isSettable ? ` le ${new Date(selectedDate.date1.startDate).toLocaleString('fr-FR')}` : ' immédiatement'}.`}
          confirmLabel={notification.isSettable ? 'Programmer' : 'Envoyer'}
          cancelLabel='Annuler'
          onConfirm={() => notify(true)}
          onClose={() => setConfirmBroadcast(false)}
        />
      </div>
    </DashboardLayout>
  )
}

export default NotificationBuilder
